/**
 * Per-vehicle map flags: chronic driving alerts + live range risk.
 *
 * A flag is a pin above the truck the dispatcher can click for detail — not another
 * list row. Severity and kind drive colour; the popup / drawer carry the evidence.
 */
import type { Alert, RouteProgress, Triage, Vehicle, VehicleMeta } from './types'
import { estimateRange, withCorridorForecast, type RangeEstimate } from './range'

export type FlagKind = 'overspeed' | 'harsh' | 'range' | 'multi'

export interface VehicleFlag {
  vehicle_id: string
  kind: FlagKind
  label: string
  severity: 'warn' | 'critical'
  detail: string
  alerts: Alert[]
  range: RangeEstimate | null
}

const OVERSPEED_KINDS = new Set([
  'OVERSPEED', 'REPEAT_OVERSPEED', 'GHAT_OVERSPEED',
])
const HARSH_KINDS = new Set([
  'HARSH_BRAKE', 'HARSH_BRAKING', 'HARSH_ACCEL', 'RASH_DRIVING',
])

/** Look-back for "keeps doing it" on the live wall. */
export const FLAG_WINDOW_MS = 45 * 60 * 1000

export function buildVehicleFlags(
  vehicles: Iterable<Vehicle>,
  alerts: readonly Alert[],
  progress: ReadonlyMap<string, RouteProgress>,
  meta: ReadonlyMap<string, VehicleMeta>,
  now = Date.now(),
  windowMs = FLAG_WINDOW_MS,
  /** Measured fleet kWh/km by load; preferred over the hardcoded prior. */
  fleet?: import('./range').FleetEfficiency,
): Map<string, VehicleFlag> {
  const since = now - windowMs
  const byVeh = new Map<string, Alert[]>()
  for (const a of alerts) {
    if (!a.vehicle_id || a.ts < since) continue
    if (!OVERSPEED_KINDS.has(a.kind) && !HARSH_KINDS.has(a.kind)) continue
    const list = byVeh.get(a.vehicle_id) ?? []
    list.push(a)
    byVeh.set(a.vehicle_id, list)
  }

  const out = new Map<string, VehicleFlag>()
  for (const v of vehicles) {
    const m = meta.get(v.vehicle_id)
    const prog = progress.get(v.vehicle_id)
    const range = withCorridorForecast(v, estimateRange(v, m, prog, now, undefined, fleet), prog)
    const recent = byVeh.get(v.vehicle_id) ?? []
    const over = recent.filter((a) => OVERSPEED_KINDS.has(a.kind))
    const harsh = recent.filter((a) => HARSH_KINDS.has(a.kind))
    // One recent hit already earns a pin; repeats / REPEAT_OVERSPEED escalate severity.
    const hasOver = over.length >= 1
    const hasHarsh = harsh.length >= 1
    const chronicOver = over.length >= 2 || over.some((a) => a.kind === 'REPEAT_OVERSPEED')
    const chronicHarsh = harsh.length >= 2
    const rangeRisk = range.range_risk

    if (!hasOver && !hasHarsh && !rangeRisk) continue

    const bits: FlagKind[] = []
    if (hasOver) bits.push('overspeed')
    if (hasHarsh) bits.push('harsh')
    if (rangeRisk) bits.push('range')

    const kind: FlagKind = bits.length > 1 ? 'multi' : bits[0]!
    const severity: 'warn' | 'critical' =
      chronicOver || chronicHarsh
      || recent.some((a) => a.severity === 'CRITICAL')
      || range.shortfall_km != null
      || (range.expected_soc_pct != null && range.expected_soc_pct < 12)
        ? 'critical'
        : 'warn'

    const parts: string[] = []
    if (hasOver) parts.push(`${over.length}× overspeed`)
    if (hasHarsh) parts.push(`${harsh.length}× harsh`)
    if (rangeRisk) {
      const where = prog?.next_hub ?? 'destination'
      // "exp SoC -6.7% at JHUL" said the right thing in a form that could not be true. When
      // the truck does not get there, say that -- and say how far short, which is the number
      // somebody acts on.
      parts.push(
        range.shortfall_km != null
          ? `won't reach ${where} — ${range.shortfall_km} km short`
          : `exp SoC ${range.expected_soc_pct ?? '?'}% at ${where}`,
      )
    }

    out.set(v.vehicle_id, {
      vehicle_id: v.vehicle_id,
      kind,
      label: kind === 'multi' ? '!'
        : kind === 'range' ? 'SoC'
          : kind === 'overspeed' ? 'SPD'
            : 'HRSH',
      severity,
      detail: parts.join(' · '),
      alerts: recent.slice(0, 8),
      range,
    })
  }
  return out
}

/**
 * Makes a vehicle's map pin sticky across recomputes, independent of the fleet-wide
 * alerts tape's own bounded size (ALERT_CAP in state/store.ts).
 *
 * buildVehicleFlags() is a pure snapshot: it only knows about alerts currently sitting
 * in that tape. On a busy corridor, a burst of unrelated alerts elsewhere can push a
 * given vehicle's own overspeed alert out of the (size-capped, not age-capped) tape well
 * before its flag's own 45-minute window is up -- the pin would then vanish mid-window
 * for a reason that has nothing to do with that truck. A pin should only go away because
 * its alert actually silenced: either triaged resolved, or genuinely aged past the
 * window -- judged from the alert's OWN remembered timestamp, not from whether the tape
 * still happens to be holding it.
 *
 * Range-risk-only flags (kind: 'range', alerts: []) are exempt from stickiness -- range
 * risk is a live property of current telemetry, not an event that can be "silenced", so
 * it is always taken fresh.
 */
export function mergeStickyFlags(
  previous: ReadonlyMap<string, VehicleFlag>,
  fresh: ReadonlyMap<string, VehicleFlag>,
  triage: ReadonlyMap<string, Triage>,
  now: number,
  windowMs = FLAG_WINDOW_MS,
): Map<string, VehicleFlag> {
  const out = new Map<string, VehicleFlag>()
  const ids = new Set([...previous.keys(), ...fresh.keys()])
  for (const id of ids) {
    const f = fresh.get(id)
    if (f) { out.set(id, f); continue }
    const p = previous.get(id)
    if (!p || !p.alerts.length) continue
    const silenced = p.alerts.every((a) => triage.get(a.alert_id)?.state === 'resolved')
    const lastSeenTs = Math.max(...p.alerts.map((a) => a.ts))
    if (!silenced && now - lastSeenTs <= windowMs) out.set(id, p)
  }
  return out
}
